import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { notify } from 'utils';
import AppLoader from '../../../../Components/AppLoader';
import { ProjectServices } from '../../../../Services/ProjectServices';
import CreateProjectForm, { nextCourseFilterRow } from '../CreateProjectForm';
import { initAssignLicenseFields } from '../AssignLicence';
import { CORPORATE_PROGRAMME, YOUTH_PROGRAMME } from '../../../../utils/programmeOptions';
import { buildLearningAccessPayload, EMPTY_ACCESS_FORM, validateLearningAccess } from '../LearningAccessSection';

const projectServices = new ProjectServices();

const asCompanyOptions = (items) => (Array.isArray(items) ? items : [])
    .filter(item => item?.uuid)
    .map(item => ({
        ...item,
        uuid: item.uuid,
        name: item?.name || '',
        provider: item?.provider || '',
        category: (item?.category || '').trim(),
    }));

const mergeSavedOptions = (options, savedItems) => {
    const byId = new Map(options.map(item => [item.uuid, item]));
    (savedItems || []).forEach(item => {
        if (!item?.uuid) return;
        const existing = byId.get(item.uuid);
        if (existing) {
            byId.set(item.uuid, {
                ...existing,
                name: existing.name || item.name || '',
                provider: existing.provider || item.provider || '',
                category: (existing.category || item.category || '').trim(),
            });
            return;
        }
        byId.set(item.uuid, {
            uuid: item.uuid,
            name: item.name || '',
            provider: item.provider || '',
            category: (item.category || '').trim(),
            count: 0,
            total_count: item.total_count || 0,
        });
    });
    return [...byId.values()];
};

const projectLicenceLimits = (companyItem, projectItem) => {
    if (!companyItem) {
        const savedCount = parseInt(projectItem?.total_count, 10) || 0;
        return {
            user_count: projectItem?.user_count || 0,
            count: savedCount,
            total_count: savedCount,
            maxCount: savedCount,
        };
    }
    const companyTotal = parseInt(companyItem.total_count, 10) || 0;
    const assignedCount = parseInt(companyItem.count, 10) || 0;
    const overAllocated = assignedCount > companyTotal;
    let remainCount = companyTotal - assignedCount;
    let totalCount = parseInt(projectItem?.total_count, 10) || 0;
    if (totalCount > companyTotal) {
        totalCount = projectItem?.user_count ? projectItem.user_count : 0;
        if (totalCount > 0) {
            totalCount = (assignedCount <= (parseInt(projectItem?.total_count, 10) || 0)) ? companyTotal : totalCount;
        }
    }
    if (totalCount) {
        remainCount += parseInt(totalCount, 10);
    }
    if (overAllocated) {
        remainCount = companyTotal;
    }
    return {
        user_count: projectItem?.user_count || 0,
        count: totalCount || 0,
        total_count: totalCount,
        maxCount: remainCount || companyTotal,
    };
};

export const initProjectFormState = {
    status: 'Open',
    project_name: '',
    company_id: '',
    company_admin_id: '',
    project_admin: '',
    description: '',
    enable_talent_access_email: false,
    is_recruitment_project: false,
    programme_type: '',
    programme_subtype: '',
    delivery_provinces: [],
    delivery_mode: '',
    programme_type_locked: false,
};

const optionValue = value => {
    if (value && typeof value === 'object') return value.code || value.value || value.id || value.uuid || '';
    return value || '';
};

const provinceCodes = value => (Array.isArray(value) ? value : [])
    .map(optionValue)
    .filter(Boolean);

const emptyLicenceForm = () => ({
    assessments: [...initAssignLicenseFields.assessments],
    courses: [...initAssignLicenseFields.courses],
    apprenticeships: [...initAssignLicenseFields.apprenticeships],
    labs: [...initAssignLicenseFields.labs],
});

/**
 * Edits one project. `section` decides which half of the project form is shown:
 * "details" (project fields) or "licences" (licence assignment). Saving always sends
 * the full project payload so the half that is not shown keeps its saved values.
 */
const ProjectEditor = forwardRef(({
    projectUuid,
    section,
    active,
    customerId,
    customerList,
    customerAdminList,
    projectAdminList,
    onProjectAdminCreated,
    programmeOptions,
}, ref) => {
    const isNew = !projectUuid;
    const [loaded, setLoaded] = useState(isNew);
    const [loading, setLoading] = useState(false);
    const [formState, setFormState] = useState(() => ({
        ...initProjectFormState,
        company_id: isNew ? (customerId || '') : '',
    }));
    const [assignLicenseForm, setAssignLicenseForm] = useState(emptyLicenceForm);
    const [assessmentList, setAssessmentList] = useState([]);
    const [apprenticeshipList, setApprenticeshipList] = useState([]);
    const [courseList, setCourseList] = useState([]);
    const [removeCourses, setRemoveCourses] = useState([]);
    const [removeApprenticeships, setRemoveApprenticeships] = useState([]);
    const [removeAssessments, setRemoveAssessments] = useState([]);
    const [removeLabs, setRemoveLabs] = useState([]);
    const [existingLicence, setExistingLicence] = useState({ courses: [], assessments: [], apprenticeships: [] });
    const [learningAccess, setLearningAccess] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [pendingAccess, setPendingAccess] = useState(EMPTY_ACCESS_FORM);
    const [planOptions, setPlanOptions] = useState([]);
    const dirty = useRef(false);
    const formRef = useRef(null);
    const rootRef = useRef(null);
    const loadStarted = useRef(false);

    const markDirty = () => {
        dirty.current = true;
    };

    const loadProject = async () => {
        setLoading(true);
        try {
            const projRes = await projectServices.getProjectDetail(projectUuid);
            if (!projRes || !projRes.status) {
                loadStarted.current = false;
                notify("error", projRes?.message || "Could not load this project.");
                return;
            }
            const responsData = projRes?.data || {};
            const { assessment, course, apprenticeship, lab: labs } = responsData;
            const companyCourses = asCompanyOptions(responsData.company_courses);
            const companyAssessments = asCompanyOptions(responsData.company_assessments);
            const companyApprenticeships = asCompanyOptions(responsData.company_apprenticeships);
            const companyCourseById = new Map(companyCourses.map(item => [item.uuid, item]));
            const companyAssessmentById = new Map(companyAssessments.map(item => [item.uuid, item]));
            const companyApprenticeshipById = new Map(companyApprenticeships.map(item => [item.uuid, item]));
            const projectCourses = [];
            const courseIds = [];
            (course || []).forEach(crsItem => {
                const itm = { ...crsItem };
                const companyCourse = companyCourseById.get(itm.uuid);
                const provider = itm.provider || companyCourse?.provider || '';
                courseIds.push(itm.uuid);
                projectCourses.push({
                    course_id: itm.uuid,
                    ...projectLicenceLimits(companyCourse, itm),
                    provider,
                    category: (itm.category || companyCourse?.category || '').trim(),
                });
            });
            const projectLabs = [];
            const mappedLabs = courseIds.length ? await projectServices.getLabsByCourse({ course_ids: courseIds }) : null;
            (mappedLabs?.data) && mappedLabs.data?.forEach((lb) => {
                const itm = { ...lb };
                const assignCourseLabs = projectCourses.find(crs => crs.course_id === itm.course_id);
                const assignLabs = labs?.find(itm_lab => itm_lab.uuid === itm.uuid);
                if (assignCourseLabs && assignLabs) {
                    projectLabs.push({
                        lab_id: itm.uuid,
                        user_count: assignLabs?.user_count || 0,
                        count: assignCourseLabs?.total_count || 0,
                        name: itm.name,
                        course_id: itm?.course_id || '',
                        total_count: assignLabs?.total_count || 0,
                        maxCount: assignCourseLabs?.remainCount || 0
                    });
                }
            });
            const projectAssessment = (assessment || []).map(ass => ({
                assessment_id: ass.uuid,
                ...projectLicenceLimits(companyAssessmentById.get(ass.uuid), ass),
            }));
            const projectApprenticeship = (apprenticeship || []).map(appr => ({
                apprenticeship_id: appr.uuid,
                ...projectLicenceLimits(companyApprenticeshipById.get(appr.uuid), appr),
            }));
            setCourseList(mergeSavedOptions(companyCourses, course));
            setAssessmentList(mergeSavedOptions(companyAssessments, assessment));
            setApprenticeshipList(mergeSavedOptions(companyApprenticeships, apprenticeship));
            setExistingLicence({
                courses: (course || []).map(item => item.uuid).filter(Boolean),
                assessments: (assessment || []).map(item => item.uuid).filter(Boolean),
                apprenticeships: (apprenticeship || []).map(item => item.uuid).filter(Boolean),
            });
            setAssignLicenseForm({
                assessments: (projectAssessment?.length) ? [...projectAssessment] : [...initAssignLicenseFields.assessments],
                courses: (projectCourses?.length) ? [...projectCourses] : [...initAssignLicenseFields.courses],
                apprenticeships: (projectApprenticeship?.length) ? [...projectApprenticeship] : [...initAssignLicenseFields.apprenticeships],
                labs: (projectLabs?.length) ? [...projectLabs] : [...initAssignLicenseFields.labs],
            });
            setFormState({
                status: 'Open',
                project_name: responsData?.project_name,
                company_id: responsData?.company_id,
                company_admin_id: optionValue(responsData?.company_admin_id),
                project_admin: optionValue(responsData?.project_admin),
                description: responsData?.description,
                enable_talent_access_email: !!responsData?.enable_talent_access_email,
                is_recruitment_project: !!responsData?.is_recruitment_project,
                programme_type: responsData?.programme_type || '',
                programme_subtype: responsData?.programme_subtype || '',
                delivery_provinces: provinceCodes(responsData?.delivery_provinces),
                delivery_mode: optionValue(responsData?.delivery_mode),
                programme_type_locked: !!responsData?.programme_type_locked,
            });
            setLearningAccess(responsData?.learning_access || null);
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (active && !isNew && !loadStarted.current) {
            loadStarted.current = true;
            loadProject();
        }
    // loadStarted guarantees one detail request when an existing editor becomes active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, isNew]);

    useEffect(() => {
        if (!isNew || !active || planOptions.length) return undefined;
        let cancelled = false;
        projectServices.getLearningAccessPlans().then((plans) => {
            if (!cancelled && plans.length) setPlanOptions(plans);
        });
        return () => { cancelled = true; };
    }, [isNew, active, planOptions.length]);

    useEffect(() => {
        if (!isNew) return;
        setFormState(prev => {
            const companyId = prev.company_id || customerId || '';
            const adminId = prev.company_admin_id
                || (customerAdminList?.length === 1 ? customerAdminList[0].uuid : '');
            if (companyId === prev.company_id && adminId === prev.company_admin_id) return prev;
            return { ...prev, company_id: companyId, company_admin_id: adminId };
        });
    }, [isNew, customerId, customerAdminList]);

    const detailErrors = () => {
        const errors = {};
        if (!formState.project_name) errors.project_name = 'Enter a project name.';
        if (!formState.company_id) errors.company_id = 'Select a company.';
        if (!formState.company_admin_id) errors.company_admin_id = 'Select a company admin.';
        const classifyingProgramme = isNew
            || !!formState.programme_type
            || !!formState.programme_subtype
            || !!formState.delivery_provinces?.length
            || !!formState.delivery_mode;
        if (classifyingProgramme && !formState.programme_type) errors.programme_type = 'Select a programme type.';
        if (formState.programme_type === YOUTH_PROGRAMME && !formState.programme_subtype) {
            errors.programme_subtype = 'Select a programme sub-type.';
        }
        if (classifyingProgramme && !formState.delivery_provinces?.length) {
            errors.delivery_provinces = 'Select at least one delivery province.';
        }
        if (classifyingProgramme && !formState.delivery_mode) errors.delivery_mode = 'Select a delivery mode.';
        return errors;
    };

    const validate = () => {
        const errors = detailErrors();
        setFieldErrors(errors);
        const messages = Object.values(errors);
        if (messages.length) {
            notify('error', messages.length === 1
                ? messages[0]
                : `${formState.project_name || 'This project'} has ${messages.length} fields to fix. They are highlighted in red.`);
            window.requestAnimationFrame(() => {
                rootRef.current?.querySelector('.Mui-error')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
            return false;
        }
        if (isNew) {
            const accessError = validateLearningAccess(pendingAccess);
            if (accessError) {
                notify('error', `Learning Hub access: ${accessError}`);
                return false;
            }
        }
        if (section !== 'licences') return true;
        const zeroAssessments = (assignLicenseForm.assessments || []).filter(itm => itm?.assessment_id && !parseInt(itm.count));
        const zeroCourses = (assignLicenseForm.courses || []).filter(itm => itm?.course_id && !parseInt(itm.count));
        const zeroApprenticeships = (assignLicenseForm.apprenticeships || []).filter(itm => itm?.apprenticeship_id && !parseInt(itm.count));
        if (zeroAssessments.length) {
            notify("error", `You can't Assign Assessment with 0 Count!`);
            return false;
        }
        if (zeroCourses.length) {
            const names = zeroCourses.map(cs => courseList.find(course => course.uuid === cs.course_id)?.name || '');
            notify("error", `You can't Assign ${names.join(", ")} Course with 0 Count!`);
            return false;
        }
        if (zeroApprenticeships.length) {
            const names = zeroApprenticeships.map(cs => apprenticeshipList.find(item => item.uuid === cs.apprenticeship_id)?.name || '');
            notify("error", `You can't Assign ${names.join(", ")} Apprenticeship with 0 Count!`);
            return false;
        }
        return true;
    };

    const save = async () => {
        if (!loaded) {
            return { ok: true, uuid: projectUuid, saved: false };
        }
        // Create an invited project admin before the project save. A new project has
        // no id yet; usercreate still runs with the customer id, then the project
        // payload stores the new admin.
        const admin = await formRef.current?.resolveProjectAdmin?.();
        if (admin && admin.ok === false) {
            return { ok: false };
        }
        const projectAdminId = admin?.id ?? formState.project_admin;
        if (!dirty.current && !admin?.created) {
            return { ok: true, uuid: projectUuid, saved: false };
        }
        if (!validate()) {
            return { ok: false };
        }
        const assignLabs = [];
        (assignLicenseForm?.labs || []).forEach((itm) => {
            const course = assignLicenseForm.courses.find(crs => crs?.course_id === itm.course_id);
            if (course) {
                assignLabs.push({
                    ...itm,
                    count: course?.count ? parseInt(course?.count) : 0,
                    total_count: course?.count ? parseInt(course?.count) : 0
                });
            }
        });
        const payload = {
            ...formState,
            project_admin: projectAdminId,
            course: (assignLicenseForm.courses || []).map(({ provider, category, ...courseRow }) => courseRow),
            assessment: assignLicenseForm.assessments,
            apprenticeship: assignLicenseForm.apprenticeships,
            lab: assignLabs,
            remove_assesments: removeAssessments,
            remove_courses: removeCourses,
            remove_labs: removeLabs,
            remove_apprenticeships: removeApprenticeships
        };
        delete payload.programme_type_locked;
        if (formState.programme_type !== YOUTH_PROGRAMME) {
            delete payload.programme_subtype;
        }
        // is_recruitment_project — omit from PUT projectdetail; re-enable when edit updates are allowed
        if (projectUuid) {
            delete payload.is_recruitment_project;
            if (!formState.programme_type) delete payload.programme_type;
            if (!formState.programme_subtype) delete payload.programme_subtype;
            if (!formState.delivery_provinces?.length) delete payload.delivery_provinces;
            if (!formState.delivery_mode) delete payload.delivery_mode;
        }
        const createRes = await projectServices.addProject(payload, projectUuid);
        if (createRes && createRes.status && !createRes?.message?.includes("You don't have availabe license")) {
            dirty.current = false;
            const savedUuid = createRes?.data?.uuid || projectUuid;
            if (isNew && savedUuid && pendingAccess.plan) {
                const accessRes = await projectServices.saveLearningAccess(
                    savedUuid,
                    buildLearningAccessPayload(pendingAccess)
                );
                if (!accessRes?.status) {
                    notify('warning', `Project created, but Learning Hub access was not saved: ${accessRes?.message || 'try again from the project.'}`);
                }
            }
            return { ok: true, uuid: savedUuid, saved: true, message: createRes.message };
        }
        notify("error", (createRes && createRes.message) ? createRes.message : 'Something Went Wrong');
        return { ok: false };
    };

    useImperativeHandle(ref, () => ({ save }));

    const handleInputChange = (evt) => {
        const { name, value, type, checked } = evt.target;
        markDirty();
        setFieldErrors(prev => {
            if (!prev[name] && !(name === 'programme_type' && prev.programme_subtype)) return prev;
            const next = { ...prev };
            delete next[name];
            if (name === 'programme_type') delete next.programme_subtype;
            return next;
        });
        setFormState(prev => {
            const nextValue = type === 'checkbox' ? checked : value;
            if (name === 'programme_type') {
                return {
                    ...prev,
                    programme_type: nextValue,
                    programme_subtype: nextValue === CORPORATE_PROGRAMME ? '' : prev.programme_subtype,
                };
            }
            return { ...prev, [name]: nextValue };
        });
    };

    const handleProjectAdminCreated = async (newAdmin, email) => {
        const selectedId = await onProjectAdminCreated(newAdmin, email);
        if (selectedId) {
            markDirty();
            setFormState(prev => ({ ...prev, project_admin: selectedId }));
        }
    };

    const applyCourseRowChange = (idx, current, nextRow) => {
        const clearedId = current?.course_id && current.course_id !== nextRow.course_id ? current.course_id : '';
        if (clearedId && existingLicence.courses?.includes(clearedId) && !removeCourses.includes(clearedId)) {
            setRemoveCourses([...removeCourses, clearedId]);
        }
        let nextLabs = assignLicenseForm.labs;
        if (clearedId) {
            const removedLabIds = [];
            const remaining = (assignLicenseForm.labs || []).filter(lab => {
                if (lab?.course_id === clearedId && lab?.lab_id) {
                    removedLabIds.push(lab.lab_id);
                    return false;
                }
                return true;
            });
            if (removedLabIds.length) {
                setRemoveLabs([...removeLabs, ...removedLabIds.filter(id => !removeLabs.includes(id))]);
            }
            nextLabs = remaining.length ? remaining : [...initAssignLicenseFields.labs];
        }
        setAssignLicenseForm(prevState => {
            const courses = [...prevState.courses];
            courses[idx] = nextRow;
            return { ...prevState, courses, labs: nextLabs };
        });
    };

    const autoSelectLabsByCourse = async (course, indx, action = 'add') => {
        const removeCourse = (!course && assignLicenseForm.courses.length) ? assignLicenseForm.courses[indx] : null;
        const courseIds = (removeCourse && removeCourse?.course_id) ? [removeCourse?.course_id] : [course];
        let selectedLabs = (assignLicenseForm.labs.length && assignLicenseForm.labs[0].lab_id) ? [...assignLicenseForm.labs] : [];
        const selectedCourse = assignLicenseForm.courses.find(cr => cr.course_id === course);
        const courseLabs = await projectServices.getLabsByCourse({ course_ids: courseIds });
        const removeLabsIds = [...removeLabs];
        const removeLabsIndex = [];
        if (courseLabs?.status) {
            courseLabs?.data.forEach(itm => {
                if (removeCourse && action !== 'add') {
                    const removeIdx = assignLicenseForm.labs.findIndex(lab => lab.lab_id === itm.uuid);
                    removeLabsIndex.push(removeIdx);
                    removeLabsIds.push(itm.uuid);
                } else {
                    const exist = selectedLabs.find((lb) => lb.lab_id === itm.uuid);
                    if (!exist) {
                        selectedLabs.push({
                            lab_id: itm.uuid,
                            name: itm.name,
                            count: selectedCourse?.count || 0,
                            total_count: selectedCourse?.total_count || 0,
                            course_id: itm?.course_id || ''
                        });
                    }
                }
            });
        }
        if (removeCourse && removeLabsIndex.length) {
            const indexSet = new Set(removeLabsIndex);
            selectedLabs = selectedLabs.filter((value, i) => !indexSet.has(i));
            setRemoveLabs(removeLabsIds);
        }
        const assignCourse = courseList.find(cr => cr.uuid === course);
        if (action === 'add' && course) {
            setRemoveCourses(prev => prev.filter(id => id !== course));
        }
        setAssignLicenseForm((prevState) => {
            const newState = { ...prevState };
            const prvData = [...newState.courses];
            if (action === 'add') {
                prvData[indx] = {
                    ...prvData[indx],
                    maxCount: assignCourse?.total_count,
                    course_id: course,
                    provider: assignCourse?.provider || prvData[indx]?.provider || '',
                    category: (assignCourse?.category || prvData[indx]?.category || '').trim(),
                };
            } else {
                prvData.splice(indx, 1);
            }
            newState.courses = (prvData.length) ? prvData : [...initAssignLicenseFields.courses];
            newState.labs = selectedLabs.length ? [...selectedLabs] : [...initAssignLicenseFields.labs];
            return newState;
        });
    };

    const handleChangeAsl = (e, idx, type) => {
        let { name, value } = e.target;
        markDirty();
        if (type === 'courses' && (name === 'provider' || name === 'category')) {
            const current = assignLicenseForm.courses?.[idx] || { ...initAssignLicenseFields.courses[0] };
            applyCourseRowChange(idx, current, nextCourseFilterRow(courseList, current, name, value));
            return;
        }
        if (type === 'courses' && name === 'course_id' && !value) {
            const current = assignLicenseForm.courses?.[idx] || { ...initAssignLicenseFields.courses[0] };
            applyCourseRowChange(idx, current, {
                ...initAssignLicenseFields.courses[0],
                provider: current.provider || '',
                category: current.category || '',
            });
            return;
        }
        if (type === 'courses' && name === 'course_id') {
            autoSelectLabsByCourse(value, idx, 'add');
        } else if (type === 'assessments' && name === 'assessment_id') {
            const selectedAssessment = assessmentList.find(ass => ass.uuid === value);
            setAssignLicenseForm((prevState) => {
                const prvData = [...prevState[type]];
                prvData[idx] = { ...prvData[idx], [name]: value, maxCount: selectedAssessment?.total_count || 0 };
                return { ...prevState, [type]: prvData };
            });
        } else if (type === 'apprenticeships' && name === 'apprenticeship_id') {
            const selectedApprenticeship = apprenticeshipList.find(app => app.uuid === value);
            setAssignLicenseForm((prevState) => {
                const prvData = [...prevState[type]];
                prvData[idx] = { ...prvData[idx], [name]: value, maxCount: selectedApprenticeship?.total_count || 0 };
                return { ...prevState, [type]: prvData };
            });
        } else {
            if (name === "count") {
                value = value ? parseInt(value) : 0;
            }
            setAssignLicenseForm((prevState) => {
                const prvData = [...prevState[type]];
                prvData[idx] = { ...prvData[idx], [name]: value };
                return { ...prevState, [type]: prvData };
            });
        }
    };

    const handleRemoveAssignLicence = (type, idx) => {
        markDirty();
        const checkExist = assignLicenseForm?.[type]?.[idx];
        const checkKey = (type === 'courses') ? 'course_id' : (type === 'apprenticeships') ? 'apprenticeship_id' : (type === 'assessments') ? 'assessment_id' : '';
        if (checkKey && checkExist[checkKey] && existingLicence[type].includes(checkExist[checkKey])) {
            if (type === 'courses') {
                setRemoveCourses([...removeCourses, checkExist?.course_id]);
            } else if (type === 'apprenticeships') {
                setRemoveApprenticeships([...removeApprenticeships, checkExist?.apprenticeship_id]);
            } else if (type === 'assessments') {
                setRemoveAssessments([...removeAssessments, checkExist?.assessment_id]);
            }
        }
        if (type === 'courses') {
            autoSelectLabsByCourse(null, idx, 'remove');
        } else {
            setAssignLicenseForm((prevState) => {
                const prvData = [...prevState[type]];
                prvData.splice(idx, 1);
                return { ...prevState, [type]: (prvData?.length) ? prvData : [...initAssignLicenseFields[type]] };
            });
        }
    };

    const handleAddAssignLicence = (type) => {
        markDirty();
        setAssignLicenseForm((prevState) => ({
            ...prevState,
            [type]: [...prevState[type], { ...initAssignLicenseFields[type][0] }],
        }));
    };

    if (loading || (!loaded && active)) {
        return <AppLoader minHeight={180} />;
    }
    if (!loaded) return null;

    return (
        <div ref={rootRef}>
        <CreateProjectForm
            ref={formRef}
            fieldErrors={fieldErrors}
            section={section}
            onDraftChange={markDirty}
            formState={formState}
            handleInputChange={handleInputChange}
            customerList={customerList}
            customerAdminList={customerAdminList}
            projectAdminList={projectAdminList}
            project_id={projectUuid || ''}
            customerId={customerId}
            onProjectAdminCreated={handleProjectAdminCreated}
            assignLicenseForm={assignLicenseForm}
            handleChangeAsl={handleChangeAsl}
            handleAddAssignLicence={handleAddAssignLicence}
            handleRemoveAssignLicence={handleRemoveAssignLicence}
            assessmentList={assessmentList}
            apprenticeshipList={apprenticeshipList}
            courseList={courseList}
            learningAccess={learningAccess}
            pendingLearningAccess={isNew ? {
                value: pendingAccess,
                plans: planOptions,
                onChange: (next) => {
                    markDirty();
                    setPendingAccess(next);
                },
            } : null}
            programmeOptions={programmeOptions}
        />
        </div>
    );
});

ProjectEditor.propTypes = {
    projectUuid: PropTypes.string,
    section: PropTypes.oneOf(['details', 'licences']).isRequired,
    active: PropTypes.bool,
    customerId: PropTypes.string,
    customerList: PropTypes.array,
    customerAdminList: PropTypes.array,
    projectAdminList: PropTypes.array,
    onProjectAdminCreated: PropTypes.func,
    programmeOptions: PropTypes.object,
};

export default ProjectEditor;
